<template>
  <div class="campaigns-template-editor">
    <div class="editmail__container">
      <div class="editmail__constructor">
        <div class="editmail__subject-container">
          <div class="editmail__subject-container-inner">
            <AkInput
              v-bind="$attrs"
              data-testid="campaignObject"
              name="campaignSubject"
              type="text"
              class="ds-mb-4"
              :model-value="message?.subject"
              :label="t('Subject')"
              :placeholder="t('Discover our new products!')"
              :errors="errors?.subject?.[0]"
              :disabled="disabled"
              :required="true"
              :maxlength="100"
              @update:model-value="emit('update:subject', $event)"
            />
            <AkParagraph
              v-if="!!message?.subject"
              size="xs"
              data-testid="campaignsCharCounter"
              class="campaigns-template-editor__char-counter"
            >
              {{ charCounter }}
            </AkParagraph>
          </div>
        </div>
        <div
          class="editmail__editor editmail__editor--menu"
          :class="{ 'error-border': errors?.body }"
        >
          <RichTextEditor
            ref="editorBody"
            :placeholder="placeholder?.body"
            :original-content="message?.body"
            :disabled="disabled"
            :identifier="bodyIdentifier"
            height="600"
            :use-media-library-service="useMediaLibrary"
            :use-tracking-service="useTracking"
            :image-cdn-service="imageCdnService"
            :use-drawer-service="useDrawer"
            @all-content-updated="emit('update-all-bodies', $event)"
            @content-updated="emit('update:body', $event)"
          />
        </div>

        <div
          v-if="errors?.body"
          class="error"
        >
          {{ errors?.body?.[0] }}
        </div>

        <template v-if="displayFixedElements">
          <div
            class="editmail__editor editmail__editor--cta text-center"
            data-testid="ctaContainer"
          >
            <span
              v-if="message?.language"
              class="button button-discount button-fake"
            >
              <input
                v-model="message.ctaText"
                type="text"
              >
            </span>

            <AkParagraph
              v-if="errors?.ctaText"
              class="error error-msg"
              role="alert"
            >
              {{ t('This field is required') }}
            </AkParagraph>
          </div>
          <div
            class="editmail__editor editmail__editor--menu"
            :class="{ 'error-border': errors?.footer }"
            data-testid="footerEditor"
          >
            <RichTextEditor
              ref="editorFooter"
              :placeholder="placeholder?.footer"
              :original-content="message?.footer"
              :disabled="disabled"
              :identifier="footerIdentifier"
              height="300"
              :use-media-library-service="useMediaLibrary"
              :use-tracking-service="useTracking"
              :image-cdn-service="imageCdnService"
              :use-drawer-service="useDrawer"
              @content-updated="emit('update:footer', $event)"
            />
          </div>
          <AkParagraph
            v-if="errors?.footer"
            class="error error-msg"
          >
            {{ errors?.footer?.[0] }}
          </AkParagraph>
        </template>
        <div
          v-if="message?.language"
          class="editmail__content"
        >
          <p id="unfollow">
            {{ localisedCopy.unsubscribeMessage }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
export default defineComponent({
  compatConfig: { COMPONENT_V_MODEL: false },
});
</script>
<script setup lang="ts">
import { computed, toRefs, watch, ref, Ref, onMounted, defineComponent } from 'vue';
import { AkInput, AkParagraph } from '@ankorstore/design-system';
import { Brand } from '@monolith/legacy/types/api/brand';
import { useStore } from '@core/composables/use-store';
import { Placeholder, MessageElement, FormErrors } from '../../types/create-campaigns.types';
import {ImgCdnImageTransformFn, imgCdnUrlFill, RichTextEditor} from "@bc/shared";
import i18nInstance, { loadLanguageAsync } from '@client/monolith/legacy/services/i18n';
import { useI18n } from 'vue-i18n';
import { useMediaLibrary } from '@client/modules/brands/domain/media-library';
import { useTracking } from '@bc/shared/composables';
import { useDrawer } from '@bc/shared';
import { CreateCampaignTemplateVersionEnum1 } from '@ankorstore/api-internal/data-contracts';

const props = withDefaults(
  defineProps<{
    errors?: FormErrors['messages'][number];
    disabled?: boolean;
    message?: MessageElement;
    placeholder?: Placeholder;
    templateVersion?: CreateCampaignTemplateVersionEnum1;
    bodyIdentifier: string;
    footerIdentifier: string;
  }>(),
  {
    errors: null,
    disabled: false,
    message: undefined,
    placeholder: undefined,
    templateVersion: undefined,
  }
);

const emit = defineEmits(['update:subject', 'update:body', 'update-all-bodies', 'update:footer']);

const store = useStore();
const { t } = useI18n();
const brand = computed((): Brand => store.getters['brand']);
const { message, errors } = toRefs(props);
const editorBody: Ref = ref(null);
const editorFooter: Ref = ref(null);

const hasLoadedEmailLocale = computed(() => {
  return i18nInstance.global.availableLocales.includes(message.value?.language);
});

const charCounter = computed(() => {
  return (message.value?.subject?.length || 0) + '/75';
});

const imageCdnService = computed(() => {
  return {
    createUrl: (filename: string, size: string) => imgCdnUrlFill(filename, size, { func: ImgCdnImageTransformFn.CROP }),
  };
});

watch(
  message,
  (newMessage) => {
    if(!newMessage){
      return;
    }
    editorBody.value?.setContent(newMessage.body);
    editorFooter.value?.setContent(newMessage.footer);
  },
  { deep: true }
);

onMounted(() => {
  // Load additional language for locale of the email currently being edited
  if(message.value?.language && !hasLoadedEmailLocale.value) {
    loadLanguageAsync(message.value.language, i18nInstance, false);
  }
});

const localisedCopy = computed(() => {
  if (!hasLoadedEmailLocale.value){
    return {};
  }

  return {
    unsubscribeMessage: t('account.brands.createCampaigns.unsubscribe.message', { brand: brand.value.name }, { locale: message?.value?.language })
  };
})

const displayFixedElements = computed(() => {
  return props.templateVersion === '1';
});
</script>

<style lang="scss" scoped>
.campaigns-template-editor {
  &__char-counter {
    @apply ds-absolute ds-right-0 ds-bottom-5 ds-p-4 ds-text-neutral-700;
  }
}

.error {
  @apply ds-text-error-700 ds-text-center ds-mt-4;
}

.error-border {
  @apply ds-border-error-700 ds-border-solid;
  border-radius: 11px;
  border-width: 1px;
}

.editmail {
  &__subject-container {
    @apply ds-flex ds-gap-2;

    &-inner {
      @apply ds-flex-1 ds-relative;
    }
  }
  &__constructor {
    max-width: 645px;
    margin: 0 auto 20px;
    @apply ds-bg-white;
    .button-fake {
      width: 100%;
      input {
        border: none;
        background: none;
        @apply ds-text-white;
        text-align: center;
        font-size: 16px;
        outline: none;
        padding: 0;
        width: 100%;
        height: auto;
        &::placeholder {
          @apply ds-text-white;
        }
      }
    }
  }
  &__content {
    p {
      margin-top: 15px;
      margin-bottom: 0;
    }
    img {
      margin-top: 0px;
      margin-bottom: 0;
      display: block;
      margin-left: auto;
      margin-right: auto;
    }
    #sendby {
      margin-top: 25px;
      margin-bottom: 25px;
      font-size: 13px;
    }
    #unfollow {
      @apply ds-text-neutral-700;
      margin-bottom: 15px;
      font-size: 12px;
    }
    #sendby,
    #unfollow {
      text-align: center;
    }
  }
  &__actions {
    display: flex;
    flex-direction: column;
    button {
      margin-bottom: 10px;
      &:disabled {
        @apply ds-text-neutral-700 ds-border ds-border-solid ds-border-neutral-700;
        &:hover {
          @apply ds-bg-white;
          @apply ds-text-neutral-700;
          cursor: not-allowed;
        }
      }
    }
  }
  &__editor {
    @apply ds-min-h-[600px];

    :deep(.tox-tinymce--disabled) {
      min-height: 600px;
      height: auto !important;
    }

    &--menu,
    &--cta {
      margin-top: 15px;
    }
    &--cta {
      a {
        @apply ds-font-basic;
        font-size: 14px;
        font-weight: bold;
      }
    }
    textarea {
      @apply ds-border-neutral-300;
      width: 100%;
      height: auto;
      resize: none;
      border-radius: 0;
      &:hover,
      &:focus {
        @apply ds-border-solid ds-border-primary;
      }
      &:focus {
        outline: none;
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
      }
    }
  }
}
</style>
